import { useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { SPECIALISATION_LABELS, YEAR_OF_STUDY_LABELS, type Specialisation, type YearOfStudy } from "@intern-seek/shared";
import { api, ApiError } from "../lib/api";
import { useSession } from "../lib/session";
import { PageHeading, Alert, Spinner, Empty, StatusPill } from "../components/ui";

export default function References() {
  const { user } = useSession();
  return user?.role === "STUDENT" ? <StudentReferences /> : <RefereeInbox />;
}

/* ---------------- Student: nominate an academic ---------------- */

function StudentReferences() {
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);

  const profile = useQuery({
    queryKey: ["student", "me"],
    queryFn: () => api.get<{ profile: any }>("/students/me"),
  });
  const referees = useQuery({
    queryKey: ["referees"],
    queryFn: () => api.get<{ referees: any[] }>("/references/referees"),
  });

  const nominate = useMutation({
    mutationFn: (body: unknown) => api.post("/references/requests", body),
    onSuccess: () => { setError(null); queryClient.invalidateQueries({ queryKey: ["student", "me"] }); },
    onError: (err) => setError(err instanceof ApiError ? err.message : "Request failed"),
  });

  if (profile.isLoading || referees.isLoading) return <Spinner />;

  const requests = profile.data?.profile?.referenceRequests ?? [];

  return (
    <>
      <PageHeading title="Academic references" description="Ask an academic staff member to act as your referee." />
      {error && <Alert tone="error">{error}</Alert>}

      <form
        className="card mb-6"
        onSubmit={(e) => {
          e.preventDefault();
          const f = new FormData(e.currentTarget);
          nominate.mutate({ refereeUserId: f.get("refereeUserId"), message: f.get("message") });
          e.currentTarget.reset();
        }}
      >
        <label className="label" htmlFor="refereeUserId">Academic staff member</label>
        <select id="refereeUserId" name="refereeUserId" required className="input mb-4">
          <option value="">Select a staff member...</option>
          {referees.data?.referees.map((r) => (
            <option key={r.id} value={r.id}>
              {r.name ?? r.email}
              {r.staffProfile?.department ? ` — ${r.staffProfile.department}` : ""}
            </option>
          ))}
        </select>

        <label className="label" htmlFor="message">Message (optional)</label>
        <textarea id="message" name="message" rows={3} className="input mb-4"
          placeholder="Remind them which paper or project you worked on together." />

        <button type="submit" className="btn-primary" disabled={nominate.isPending || !profile.data?.profile}>
          {nominate.isPending ? "Sending..." : "Send request"}
        </button>
        {!profile.data?.profile && <p className="field-error">Complete your EOI before nominating a referee.</p>}
      </form>

      <h2 className="mb-3 text-lg font-semibold text-heading">Your requests</h2>
      {requests.length === 0 ? (
        <Empty>You have not asked anyone yet.</Empty>
      ) : (
        <ul className="space-y-3">
          {requests.map((r: any) => (
            <li key={r.id} className="card flex items-center justify-between gap-4">
              <div>
                <p className="font-medium">{r.referee.name ?? r.referee.email}</p>
                <p className="text-sm text-uoa-grey">
                  Asked {new Date(r.requestedAt).toLocaleDateString("en-NZ")}
                  {r.status === "SUBMITTED" && r.confidential && " · reference is confidential"}
                </p>
              </div>
              <StatusPill status={r.status} />
            </li>
          ))}
        </ul>
      )}
    </>
  );
}

/* ---------------- Staff: respond to requests ---------------- */

function RefereeInbox() {
  const { data, isLoading } = useQuery({
    queryKey: ["references", "inbox"],
    queryFn: () => api.get<{ requests: any[] }>("/references/inbox"),
  });

  if (isLoading) return <Spinner />;
  const requests = data?.requests ?? [];

  return (
    <>
      <PageHeading title="Reference requests" description="Students who have nominated you as a referee." />
      {requests.length === 0 ? (
        <Empty>No requests yet.</Empty>
      ) : (
        <ul className="space-y-4">{requests.map((r) => <RequestCard key={r.id} request={r} />)}</ul>
      )}
    </>
  );
}

function RequestCard({ request }: { request: any }) {
  const queryClient = useQueryClient();
  const fileInput = useRef<HTMLInputElement>(null);
  const [error, setError] = useState<string | null>(null);
  const invalidate = () => queryClient.invalidateQueries({ queryKey: ["references", "inbox"] });
  const onError = (err: unknown) => setError(err instanceof ApiError ? err.message : "Something went wrong");

  const submit = useMutation({ mutationFn: (body: unknown) => api.post(`/references/requests/${request.id}/submit`, body), onSuccess: invalidate, onError });
  const decline = useMutation({ mutationFn: () => api.post(`/references/requests/${request.id}/decline`), onSuccess: invalidate, onError });
  const upload = useMutation({ mutationFn: (file: File) => api.upload(`/files/reference-letter/${request.id}`, file), onSuccess: invalidate, onError });

  const s = request.studentProfile;
  const pending = request.status === "REQUESTED";

  return (
    <li className="card">
      <div className="mb-3 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="font-semibold text-heading">{s.firstName} {s.lastName}</h2>
          <p className="text-sm text-uoa-grey">
            {SPECIALISATION_LABELS[s.specialisation as Specialisation]} · {YEAR_OF_STUDY_LABELS[s.yearOfStudy as YearOfStudy]} · ID {s.studentId}
          </p>
        </div>
        <StatusPill status={request.status} />
      </div>

      {request.message && <p className="mb-3 rounded bg-uoa-sand p-3 text-sm italic">"{request.message}"</p>}
      {error && <Alert tone="error">{error}</Alert>}

      <p className="mb-4 text-sm">
        {s.resumeFileId ? (
          <a className="text-uoa-blue underline dark:text-uoa-blue-light" href={`/api/files/${s.resumeFileId}`} target="_blank" rel="noreferrer">
            View their CV
          </a>
        ) : (
          <span className="text-uoa-grey">No CV uploaded.</span>
        )}
      </p>

      {pending ? (
        <form
          className="space-y-4 border-t border-line pt-4"
          onSubmit={(e) => {
            e.preventDefault();
            const f = new FormData(e.currentTarget);
            submit.mutate({
              recommend: f.get("recommend") === "yes",
              comments: f.get("comments"),
              confidential: f.get("confidential") === "on",
            });
          }}
        >
          <fieldset>
            <legend className="label">Would you recommend this student for an internship?</legend>
            <label className="mr-4 text-sm"><input type="radio" name="recommend" value="yes" required className="mr-1" /> Yes</label>
            <label className="text-sm"><input type="radio" name="recommend" value="no" className="mr-1" /> No</label>
          </fieldset>

          <div>
            <label className="label" htmlFor={`comments-${request.id}`}>Comments (optional)</label>
            <textarea id={`comments-${request.id}`} name="comments" rows={3} className="input" />
          </div>

          <div>
            <span className="label">Supporting letter (optional, PDF, max 5 MB)</span>
            {request.letterFile ? (
              <p className="mb-2 text-sm">Attached: {request.letterFile.filename}</p>
            ) : null}
            <input
              ref={fileInput}
              type="file"
              accept="application/pdf"
              aria-label="Upload a reference letter"
              className="block w-full text-sm file:mr-4 file:rounded-md file:border-0 file:bg-uoa-blue file:px-3 file:py-1.5 file:text-white"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) upload.mutate(file);
                if (fileInput.current) fileInput.current.value = "";
              }}
              disabled={upload.isPending}
            />
          </div>

          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" name="confidential" defaultChecked />
            Keep this reference confidential from the student
          </label>

          <div className="flex gap-3">
            <button type="submit" className="btn-primary" disabled={submit.isPending}>Submit reference</button>
            <button type="button" className="btn-secondary" onClick={() => decline.mutate()} disabled={decline.isPending}>
              Decline
            </button>
          </div>
        </form>
      ) : (
        <p className="border-t border-line pt-4 text-sm text-uoa-grey">
          {request.status === "SUBMITTED"
            ? `You ${request.recommend ? "recommended" : "did not recommend"} this student on ${new Date(request.respondedAt).toLocaleDateString("en-NZ")}.`
            : "You declined this request."}
        </p>
      )}
    </li>
  );
}
